import moment from 'moment-timezone';
import { handleError } from '../global/error_handler';
import handlingErrors from './requestErrorMessage';
import {
  currentRecordRequestForBegin,
  currentRecordRequestForSelectionSaveBegin,
  initialCurrentRecordRequest,
  selectionSaveMatchesCurrentRecordRequest,
  settleCurrentRecordSelectionSave,
  settledCurrentRecordRequest,
  terminalMatchesCurrentRecordRequest,
} from './currentRecordRequest';
import {
  canonicalPatientRootFields,
  EMAIL_ATTACHMENT_PATIENT_COLLECTION_KEYS,
  patchCanonicalPatientMetadata,
  patientMetadataFromResponse,
  teamsResponseFromPatientUpdate,
} from '../utils/emailAttachmentPatientMetadata';

// Is this document-comments response still the one being waited for?
//
// The response body identifies no record, so a late answer for the record the user
// just left used to be written straight into the shared store — record A's comments
// sitting under record B's header, one patient's notes attributed to another. The
// viewer does not unmount between records, so switching records mid-flight is the
// ordinary path, not an edge case.
//
// `fetchDocumentComments` tags its lifecycle actions with the token it asked about;
// `selectedRecordDetails.send_token` is the record actually open, updated by
// SET_SELECTED_RECORD_DETAILS at the moment of the switch. Same discipline as
// terminalMatchesCurrentRecordRequest, and it belongs HERE rather than in the
// component: a component-level guard can refuse to READ a stale response, but the
// thunk has already dispatched it by then and nothing outside the reducer can
// decline the write.
const documentCommentsResponseIsCurrent = (state, action) => {
  const requested = action.meta?.sendToken;
  // Untagged: a caller that predates the tag. Accepted rather than dropped, so this
  // guard can never make comments disappear for a path it does not know about.
  if (!requested) return true;
  return requested === state.selectedRecordDetails?.send_token;
};

// The detail rows the redesigned General tab renders, blanked whenever the viewer starts
// pointing at a different record.
//
// A constant rather than a literal because TWO dispatches have to do it, and only one of
// them used to. BEGIN_GET_RECORD_DETAILS is the fetch starting.
// SET_SELECTED_RECORD_DETAILS is the row click, and it lands FIRST — it clears the PDF and
// the in-flight request but left these behind, and React commits a render before the
// viewer's effect dispatches BEGIN. That was one paint of the previous patient's email,
// address and insurance under the newly selected record's header.
//
// patientState matters most: buildAddressLines composes the four address columns, so a
// survivor among cleared siblings renders a mangled composite rather than a stale value.
// The extraction's own values, keyed by the viewer's field-registry names.
//
// Kept as ONE object rather than flattened into sibling store fields, deliberately: these
// must never sit next to the editable columns where a mapStateToProps could pick up the
// wrong one. The AI Fields tab reads this and nothing else, so "what the AI said" cannot
// drift into "what the user last typed".
//
// Built here rather than imported from the panel's registry because reducers/records may
// not import from the feature tree — the structural test enforces that in both directions.
const mapAiExtraction = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  return {
    patientName: payload.patient_name,
    patientDOB: payload.patient_dob,
    patientPhoneNumber: payload.patient_phone_number,
    patientGender: payload.patient_gender,
    patientEmail: payload.patient_email,
    patientAddress: payload.patient_address,
    patientCity: payload.patient_city,
    patientState: payload.patient_state,
    patientZipCode: payload.patient_zip_code,
    providerOffice: payload.provider_office,
    practitionerName: payload.practitioner_name,
    referringProvider: payload.referring_provider,
    primaryPlan: payload.primary_plan,
    primaryMemberId: payload.primary_member_id,
    secondaryPlan: payload.secondary_plan_name,
    secondaryMemberId: payload.secondary_member_id,
  };
};

const CLEARED_RECORD_DETAIL_FIELDS = {
  patientName: '',
  patientFirstName: '',
  patientMiddleName: '',
  patientLastName: '',
  patientDOB: '',
  patientMrn: '',
  patientGender: '',
  patientPhoneNumber: '',
  patientEmail: '',
  patientAddress: '',
  patientZipCode: '',
  patientCity: '',
  patientState: '',
  practitionerName: '',
  providerOffice: '',
  referringProvider: '',
  primaryPlan: '',
  primaryMemberId: '',
  secondaryPlanName: '',
  secondaryMemberId: '',
  documentTitle: '',
  documentCategory: '',
  appointmentDate: '',
  // null, not {}: the AI tab distinguishes "no extraction on this record" from "an
  // extraction that produced nothing", and a stale extraction outliving its record would
  // attribute one patient's extracted values to another exactly as the columns above did.
  aiExtraction: null,
};

const patchTeamState = (state, teamsResponse) => {
  const token = teamsResponse?.send_token;
  if (!token) return state;
  const patchTeam = (record) => (
    record.send_token === token ? { ...record, team: teamsResponse.team } : record
  );
  return {
    ...state,
    received_data: (state.received_data || []).map(patchTeam),
    selectedRecordDetails: (state.selectedRecordDetails
      && state.selectedRecordDetails.send_token === token)
      ? { ...state.selectedRecordDetails, team: teamsResponse.team }
      : state.selectedRecordDetails,
  };
};

const normalizeSignatureData = (signatureData) => {
  if (Array.isArray(signatureData)) {
    return signatureData;
  }

  if (Array.isArray(signatureData?.signatures)) {
    return signatureData.signatures;
  }

  if (typeof signatureData === 'string') {
    try {
      const parsedSignatureData = JSON.parse(signatureData);
      return normalizeSignatureData(parsedSignatureData);
    } catch {
      return [];
    }
  }

  return [];
};

const RECORD_PRIVACY_COLLECTION_KEYS = [
  'uploaded_data',
  'sent_data',
  'received_data',
  'assigned_data',
  'referred_data',
  'voice_data',
];

const recordPrivacyCollectionKeysForMode = (mode) => {
  const collectionKeyByMode = {
    uploaded: 'uploaded_data',
    sent: 'sent_data',
    received: 'received_data',
    assigned: 'assigned_data',
    referred: 'referred_data',
    referral: 'received_data',
    voice: 'voice_data',
  };
  const collectionKey = collectionKeyByMode[mode];
  return collectionKey ? [collectionKey] : RECORD_PRIVACY_COLLECTION_KEYS;
};

const initialState = {
  isLoading: false,
  retrying: false,
  retryAttemptNumber: 0,
  data: [],
  uploaded_data: [],
  sent_data: [],
  received_data: [],
  assigned_data: [],
  referred_data: [],
  voice_data: [],
  department_users: [],
  archived_records: [],
  currentDocumentSource: null,
  currentRecordRequest: initialCurrentRecordRequest,
  patientName: '',
  patientFirstName: '',
  patientMiddleName: '',
  patientLastName: '',
  patientDOB: '',
  patientMrn: '',
  patientGender: '',
  patientAddress: '',
  patientZipCode: '',
  patientCity: '',
  practitionerName: '',
  patientPhoneNumber: '',
  payerName: '',
  groupNumber: '',
  primaryMemberId: '',
  secondaryPayerName: '',
  primaryPlanName: '',
  secondaryPlanName: '',
  secondaryMemberId: '',
  secondaryGroupNumber: '',
  referralNote: '',
  secure_link: '',
  secure_signed_url: '',
  fileToken: '',
  sendToken: null,
  appointmentDate: '',
  // uploaded, requested, or sent?
  mode: null,
  error: false,
  errorMessage: null,
  archiveErrorMessage: '',
  canSend: false,
  guestAccess: '',
  emailValidMessage: '',
  updatedTag: false,
  downloadUrl: '',
  downloadFilename: '',
  privacyLevel: '',
  canEditPatientInfo: false,
  canEditPrivacyLevel: false,
  hasResponse: false,
  canEditRecord: false,
  recordHasUnsavedEdits: false,
  signatureData: [],
  attachmentsData: [],
  signatureRequired: false,
  shouldFlatten: false,
  nutrientViewer: false,
  nameToSignatureRequired: false,
  isFormDocument: false,
  isFormSubmission: false,
  isEmailSubmission: false,
  documentType: 'medical_record',
  updatedLabel: false,
  updatedStatus: false,
  emailAttachmentPatientLatestRequestIds: {},
  selectedLabels: [],
  receiverType: '',
  receiver: '',
  hasWrongExtractionBeenReported: false,
  hasWrongLabelingBeenReported: false,
  isLabelEdited: false,
  numPages: null,
  archived: false,
  senderContact: '',
  senderName: '',
  apiCredentials: false,
  senderOrganization: '',
  receivedAt: '',
  callerName: '',
  createdAt: '',
  nextSendToken: null,
  nextFileToken: null,
  assignedRecord: {},
  assignedDocumentId: '',
  currentReferralStatus: null,
  referralReplies: {},
  currentRecordId: null,
  selectedRecordDetails: {},
  optedOut: false,
  isDepartmentUser: true,
  referredDocumentStatusId: null,
  referralStatusCounts: [],
  referralStatusCountsLoading: false,
  referralStatusCountsError: false,
  referralStatusCountsTotal: 0,
  referralStatusCountsUpdatedAt: null,
  referralStatusCountsRefreshNonce: 0,
  inboxRefreshNonce: 0,
  documentComments: [],
  documentCommentsLoading: false,
  ownerType: null,
  ownerId: null,
  icdCodes: '',
  primaryInsurancePoBox: '',
  primaryInsuranceState: '',
  secondaryInsurancePoBox: '',
  secondaryInsuranceState: '',
  marketingRep: '',
  authorizationNumber: '',
  authorizationFromDate: '',
  authorizationToDate: '',
  authorizationNumberOfVisits: null,
  authorizationDescription: '',
  noActiveFax: false,
  noActiveEmail: false,
  departmentName: '',
  emrIntegrationCredentialId: null,
  hasSplitSuggestion: false,
  isMedsenderUser: false,
};

const failedState = {
  ...initialState,
  error: true,
};

function reduceRecords(state = initialState, action) {
  switch (action.type) {
    case 'USER_LOGGED_OUT':
      return initialState;
    case 'BEGIN_GET_UPLOADED_RECORDS':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'uploaded',
        errorMessage: null,
        // Clear other page data when switching to uploaded
        sent_data: [],
        received_data: [],
        assigned_data: [],
        referred_data: [],
      };
    case 'FAILED_GET_UPLOADED_RECORDS':
      return {
        ...failedState,
        mode: 'uploaded',
        errorCode: action.payload.response && action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to get uploaded records'),
      };
    case 'FINISH_GET_UPLOADED_RECORDS': {
      if (typeof action.payload.data === 'object' && !Array.isArray(action.payload.data) && action.payload.data !== null) {
        return {
          ...state,
          isLoading: false,
          data: [],
          mode: 'uploaded',
          uploaded_data: [],
          errorMessage: action.payload.data.message,
        };
      }
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        uploaded_data: action.payload.data,
        headers: action.payload.headers,
        mode: 'uploaded',
      };
    }
    case 'BEGIN_DUPLICATE_RECORD':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'uploaded',
        errorMessage: null,
      };
    case 'FAILED_DUPLICATE_RECORD':
      return {
        ...failedState,
        mode: 'uploaded',
        errorCode: action.payload.response && action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to copy record'),
      };
    case 'FINISH_DUPLICATE_RECORD':
      return {
        ...state,
        isLoading: false,
        mode: 'uploaded',
        currentDocumentSource: null,
        fileToken: action.payload.data.file_token,
      };
    case 'RETRIEVED_RECORD':
      return {
        ...state,
        isLoading: false,
        currentDocumentSource: action.payload,
      };
    case 'BEGIN_GET_RECORD_DETAILS':
      if (action.meta?.requestPurpose === 'renewal') {
        return {
          ...state,
          currentRecordRequest: currentRecordRequestForBegin(action),
        };
      }
      return {
        ...state,
        currentDocumentSource: null,
        // Tokenized Chrome ownership supersedes any legacy annotation save.
        // Its late terminal is ignored, so clear the legacy shared spinner now.
        ...(action.meta?.requestOwnership && { isLoading: false }),
        // Payload-authoritative (R24), same as BEGIN_GET_SECURE_URL: reset so a
        // prior record's org flag can't linger; FINISHED sets it from the
        // response. RecordViewerIframe gates the viewer mount on currentDocumentSource,
        // so the pinned engine always reads the resolved value, not this default.
        nutrientViewer: false,
        currentRecordRequest: currentRecordRequestForBegin(action),
        sendToken: '',
        mode: 'uploaded',
        isMedsenderUser: false,
        emrIntegrationCredentialId: null,
        // Editing is a capability supplied by the matching detail response.
        // Never leave the prior record editable while a new owned request is
        // loading.
        ...(action.meta?.requestOwnership && { canEditRecord: false, fileToken: '' }),
        ...CLEARED_RECORD_DETAIL_FIELDS,
      };
    case 'FAILED_GET_RECORD_DETAILS': {
      if (!terminalMatchesCurrentRecordRequest(state.currentRecordRequest, action)) return state;

      if (action.meta?.cancelled) {
        return {
          ...state,
          currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
            error: false,
            errorMessage: null,
            errorCode: null,
            completed: false,
          }),
        };
      }

      // Ignore aborted/canceled requests - they're expected during rapid navigation
      // and another request is likely in flight for the current record
      const isAbortError = action.payload?.code === 'ERR_CANCELED'
        || action.payload?.name === 'AbortError'
        || action.payload?.name === 'CanceledError'
        || action.payload?.__CANCEL__ === true
        || /aborted|abort|canceled|cancelled/i.test(action.payload?.message || '');
      if (isAbortError) {
        // Set isLoading to false when request is aborted (e.g., user closed viewer)
        // to prevent infinite loading spinner in list view
        return {
          ...state,
          currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
            error: false,
            errorMessage: null,
            errorCode: null,
            completed: false,
          }),
        };
      }

      const responseStatus = action.payload?.response?.status;
      if (action.meta?.requestPurpose === 'renewal') {
        return {
          ...state,
          currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
            error: false,
            errorMessage: null,
            errorCode: null,
            completed: true,
          }),
        };
      }
      return {
        ...state,
        currentDocumentSource: null,
        currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
          error: true,
          errorMessage: handlingErrors(responseStatus, 'Unable to access record'),
          errorCode: responseStatus,
          completed: false,
        }),
      };
    }
    case 'FINISHED_GET_RECORD_DETAILS': {
      if (!terminalMatchesCurrentRecordRequest(state.currentRecordRequest, action)) return state;

      const responseData = action.payload?.data;
      if (action.meta?.requestPurpose === 'renewal') {
        return {
          ...state,
          currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
            error: false,
            errorMessage: null,
            errorCode: null,
            completed: true,
          }),
          ...(responseData?.signed_url ? { currentDocumentSource: responseData.signed_url } : {}),
        };
      }
      if (action.meta?.requestOwnership
        && (!responseData || typeof responseData !== 'object' || Array.isArray(responseData))) {
        return {
          ...state,
          currentDocumentSource: null,
          currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
            error: false,
            errorMessage: null,
            errorCode: null,
            completed: true,
          }),
        };
      }

      if (!action.meta?.requestOwnership) {
        // Legacy callers do not carry the viewer ownership tuple, so retain their
        // existing response-ID guard. Tokenized Chrome requests have already
        // passed the attempt/route/generation gate above; stale legacy viewer
        // state must not prevent their matching terminal from settling.
        const responseFileToken = responseData?.file_token;
        const responseSendToken = responseData?.send_token;
        const isFromSentPage = state.sent_data && state.sent_data.length > 0;

        if (isFromSentPage && responseSendToken) {
          if (state.selectedRecordDetails?.send_token
            && responseSendToken !== state.selectedRecordDetails.send_token) {
            return {
              ...state,
              currentRecordRequest: {
                ...state.currentRecordRequest,
                loading: false,
              },
            };
          }
        } else if (state.currentRecordId && responseFileToken
          && responseFileToken !== state.currentRecordId) {
          return {
            ...state,
            currentRecordRequest: {
              ...state.currentRecordRequest,
              loading: false,
            },
          };
        }
      }

      return {
        ...state,
        uploaded_data: state.uploaded_data,
        sent_data: state.sent_data,
        currentRecordRequest: settledCurrentRecordRequest(state.currentRecordRequest, action, {
          error: false,
          errorMessage: null,
          errorCode: null,
          completed: true,
        }),
        mode: 'uploaded',
        fileToken: action.payload.data.file_token,
        currentDocumentSource: action.payload.data.signed_url,
        // Org-scoped nutrient_viewer flag from records#show (render_module).
        // Boolean-guarded like FINISHED_GET_SECURE_URL so a response branch that
        // omits it keeps the prior value rather than forcing false. Delivered as
        // the authoritative prop to ViewerContainer for the iframe surface, whose
        // partitioned localStorage can't see the login-time flags cache.
        nutrientViewer: typeof action.payload.data.nutrient_viewer === 'boolean'
          ? action.payload.data.nutrient_viewer
          : state.nutrientViewer,
        downloadFilename: action.payload.data.download_filename || '',
        sendToken: action.payload.data.send_token,
        patientName: action.payload.data.patient_name,
        patientDOB: action.payload.data.patient_dob,
        patientMrn: action.payload.data.mrn,
        patientGender: action.payload.data.patient_gender,
        patientPhoneNumber: action.payload.data.patient_phone_number,
        patientAddress: action.payload.data.patient_address,
        patientZipCode: action.payload.data.patient_zip_code,
        referralNote: action.payload.data.referral_note,
        appointmentDate: action.payload.data.appointment_date,
        referringProvider: action.payload.data.referring_provider,
        referringProviderOffice: action.payload.data.provider_office,
        patientCity: action.payload.data.patient_city,
        fileTitle: action.payload.data.document_title,
        patientState: action.payload.data.patient_state,
        patientEmail: action.payload.data.patient_email,
        // The extraction's values, kept apart from the editable columns around them.
        aiExtraction: mapAiExtraction(action.payload.data.ai_extraction),
        patientFirstName: action.payload.data.patient_first_name,
        patientMiddleName: action.payload.data.patient_middle_name,
        patientLastName: action.payload.data.patient_last_name,
        providerOffice: action.payload.data.provider_office,
        primaryPlan: action.payload.data.primary_plan,
        primaryMemberId: action.payload.data.primary_member_id,
        payerName: action.payload.data.payer_name,
        groupNumber: action.payload.data.group_number,
        secondaryPayerName: action.payload.data.secondary_payer_name,
        primaryPlanName: action.payload.data.primary_plan_name,
        secondaryPlanName: action.payload.data.secondary_plan_name,
        secondaryMemberId: action.payload.data.secondary_member_id,
        secondaryGroupNumber: action.payload.data.secondary_group_number,
        icdCodes: action.payload.data.icd_codes,
        primaryInsurancePoBox: action.payload.data.primary_insurance_po_box,
        primaryInsuranceState: action.payload.data.primary_insurance_state,
        secondaryInsurancePoBox: action.payload.data.secondary_insurance_po_box,
        secondaryInsuranceState: action.payload.data.secondary_insurance_state,
        marketingRep: action.payload.data.marketing_rep,
        authorizationNumber: action.payload.data.authorization_number,
        authorizationFromDate: action.payload.data.authorization_from_date,
        authorizationToDate: action.payload.data.authorization_to_date,
        authorizationNumberOfVisits: action.payload.data.authorization_number_of_visits,
        authorizationDescription: action.payload.data.authorization_description,
        dateOfService: action.payload.data.date_of_service,
        practitionerName: action.payload.data.practitioner_name,
        selectedLabels: action.payload.data.labels,
        canSend: action.payload.data.can_send,
        privacyLevel: action.payload.data.privacy_level,
        canEditPrivacyLevel: action.payload.data.can_edit_privacy_level,
        canEditPatientInfo: action.payload.data.can_edit_patient_info,
        canEditRecord: action.payload.data.can_edit,
        isFormDocument: action.payload.data.is_form_document,
        isFormSubmission: action.payload.data.is_jotorm_submission,
        isEmailSubmission: action.payload.data.is_email_submission,
        senderContact: action.payload.data.sender_contact,
        senderName: action.payload.data.sender_name,
        senderOrganization: action.payload.data.sender_organization,
        receiverType: action.payload.data.receiver_type,
        receiver: action.payload.data.receiver,
        aiStatus: action.payload.data.ai_status,
        hasWrongExtractionBeenReported:
          action.payload.data.has_wrong_extraction_been_reported,
        hasWrongLabelingBeenReported:
          action.payload.data.has_wrong_labeling_been_reported,
        patientInfoEdited: action.payload.data.patient_info_edited,
        isLabelEdited: action.payload.data.is_label_edited,
        failedColumn: action.payload.data.failed_column,
        numPages: action.payload.data.num_pages,
        archived: action.payload.data.archived,
        receivedAt: action.payload.data.received_at,
        callerName: action.payload.data.caller_name,
        documentTitle: action.payload.data.document_title,
        documentCategory: action.payload.data.document_category,
        createdAt: action.payload.data.created_at,
        apiCredentials: action.payload.data.api_credentials,
        emrIntegrationCredentialId: action.payload.data.emr_integration_credential_id,
        ownerType: action.payload.data.owner_type,
        ownerId: action.payload.data.owner_id,
        hasSplitSuggestion: action.payload.data.has_split_suggestion,
        chartFiled: action.payload.data.chart_filed,
        isMedsenderUser: action.payload.data.is_medsender_user || false,
      };
    }
    case 'BEGIN_GET_SENT_RECORDS':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'sent',
        errorMessage: null,
        // Clear other page data when switching to sent
        uploaded_data: [],
        received_data: [],
        assigned_data: [],
        referred_data: [],
      };
    case 'FAILED_GET_SENT_RECORDS':
      return {
        ...failedState,
        mode: 'sent',
        errorMessage: handlingErrors(action.payload.response.status, 'failed to get sent records'),
        errorCode: action.payload.response.status,
      };
    case 'FINISHED_GET_SENT_RECORDS': {
      if (typeof action.payload.data === 'object' && !Array.isArray(action.payload.data) && action.payload.data !== null) {
        return {
          ...state,
          isLoading: false,
          data: [],
          mode: 'sent',
          errorMessage: action.payload.data.message,
        };
      }
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        sent_data: action.payload.data,
        mode: 'sent',
        headers: action.payload.headers,
      };
    }
    case 'BEGIN_ARCHIVE_RECORDS':
      return {
        ...state,
        isLoading: true,
        error: false,
        archiveErrorMessage: '',
      };
    case 'FAILED_ARCHIVE_RECORDS':
      // Spreads `state`, NOT `failedState`. A failed archive does not invalidate the
      // list we already fetched, but `failedState` is `{ ...initialState, error: true }`,
      // so it wiped `data`/`uploaded_data` and every other collection. Combined with
      // the `errorMessage` this case used to set, RecordsHOC then rendered
      // <ErrorDisplay> *instead of* the inbox table (RecordsHOC.jsx:1157/1164) — a
      // blank inbox reading "Failed to get archived records."
      //
      // That used to be self-repairing: the confirmation modal reloaded the page
      // unconditionally 2.5s later. The inbox now archives without a modal and skips
      // the reload while inbox_background_refresh is on, so the wipe would be terminal
      // and a manual refresh the only way back.
      //
      // `errorMessage`/`errorCode` are the INBOX-level error channel and are
      // deliberately not set here: a failed archive is reported through
      // `archiveErrorMessage`, which is what the archive modals read. Hijacking the
      // inbox channel for it is what replaced the table.
      //
      // `error` is deliberately NOT set either, for the same reason one step further
      // out. It is a slice-wide flag with many readers and no archive writer reads it
      // any more — every one of them reads the resolved result or `archiveErrorMessage`.
      // Setting it used to be laundered by the confirmation modal's unconditional 2.5s
      // reload; now that the list surfaces archive without a modal and return early on
      // failure, nothing clears it (the refresh runs from `crud`'s success-only
      // callback), so it would leak into unrelated readers — e.g.
      // `referralReplySidebar` renders <ErrorDisplay> straight off `state.records.error`
      // at render time, so a failed archive would break the Referral Replies sidebar.
      // FAILED_UNARCHIVE_RECORD follows the same rule below, because unarchive
      // callers now read the resolved request result instead of the global flag.
      return {
        ...state,
        isLoading: false,
        archiveErrorMessage: action.payload.response?.data?.error
          || action.payload.response?.statusText
          || action.payload.message,
      };
    case 'FINISHED_ARCHIVE_RECORDS':
      return {
        ...state,
        isLoading: false,
        // Cleared on success as well as on BEGIN: otherwise a failed archive's message
        // outlives it and the next successful archive reads it back as its own error.
        archiveErrorMessage: '',
      };
    case 'BEGIN_UNARCHIVE_RECORD':
      return {
        ...state,
        isLoading: true,
        error: false,
        // Cleared here as well as `error`, because `archiveErrorMessage` is the one
        // piece of archive state no inbox fetch resets (it is written only by the three
        // ARCHIVE_RECORDS cases), so it survives a page/filter change that clears
        // `error`. The list surfaces no longer read it — they derive their outcome from
        // the resolved result — but the modal-based archive surfaces still render it
        // whenever it is set (ArchiveRecord.jsx / ReferralArchiveWarning.jsx, via
        // ArchiveRecordDialog / ReferralArchiveWarningDialog). Starting an unarchive is
        // the user moving on from that failed archive, so drop the stale message now;
        // otherwise the next archive modal opens already showing an error left over from
        // an unrelated earlier attempt. The confirmation modal's reload used to wipe
        // this for us; the list surfaces no longer reload.
        archiveErrorMessage: '',
      };
    case 'FAILED_UNARCHIVE_RECORD':
      // The unarchive twin of the FAILED_ARCHIVE_RECORDS fix above, and it went
      // wrong the same three ways.
      //
      // 1. It spread `failedState` (`{ ...initialState, error: true }`), which reset
      //    `uploaded_data` to []. A failed unarchive does not invalidate the list we
      //    already fetched. `received_data` was carried over by hand, so only the
      //    uploads inbox lost its rows — the hand-patch is the tell that the spread
      //    was wrong to begin with.
      // 2. It set `errorMessage`, which is the INBOX-level error channel, not a
      //    per-action one. RecordsHOC renders <ErrorDisplay> *instead of*
      //    <InboxTable> whenever it is set (RecordsHOC.jsx:1157/1164), so one failed
      //    unarchive replaced the whole table — on the redesigned pages too, since
      //    they wrap the same HOC. Nothing repairs it: the page only reloads while
      //    `inbox_background_refresh` is off, and REQUEST_INBOX_TABLE_REFRESH is a
      //    `crud` success callback, so it never fires on this path.
      // 3. `handlingErrors(action.payload.response.error, ...)` passed `.error`
      //    where every sibling case passes `.status`, so the message was always the
      //    fallback string, and `action.payload.response.status` was dereferenced
      //    unguarded — a throw here rejects the caller's await before it can report
      //    anything.
      //
      // No `unarchiveErrorMessage` is introduced: every unarchive caller now derives
      // its outcome from the resolved result (util/archiveOutcome), so nothing would
      // read it.
      return {
        ...state,
        isLoading: false,
      };
    case 'FINISHED_UNARCHIVE_RECORD':
      return {
        ...state,
        isLoading: false,
      };
    case 'FINISHED_REVOKE_RECORD':
      return {
        ...state,
        isLoading: false,
        mode: 'uploaded',
      };
    case 'BEGIN_UPLOAD_RECORD':
      return {
        ...state,
        currentDocumentSource: null,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_UPLOAD_RECORD': {
      const error = action.payload;
      const errorMessage = error.response.status === 409
        ? handleError(error)
        : error.response.status === 422
          ? (error.response?.data?.error || 'Failed to upload record. Please try again.')
          : 'Failed to upload record. Please try again.';
      return {
        ...failedState,
        errorMessage,
        errorCode: action.payload.response.status,
      };
    }
    case 'FINISHED_UPLOAD_RECORD':
      return {
        ...state,
        data: action.payload.data,
        isLoading: false,
        patientName: action.payload.data.patient_name,
        patientFirstName: action.payload.data.patient_first_name,
        patientMiddleName: action.payload.data.patient_middle_name,
        patientLastName: action.payload.data.patient_last_name,
        patientDOB: action.payload.data.patient_dob,
        patientMrn: action.payload.data.mrn,
      };
    case 'BEGIN_REVOKE_ACCESS':
      return {
        ...state,
        isLoading: true,
        error: false,
        mode: 'revoke',
        errorMessage: null,
      };
    case 'FAILED_REVOKE_ACCESS':
      return {
        ...failedState,
        uploaded_data: state.uploaded_data,
        sent_data: state.sent_data,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to revoke access.'),
        errorCode: action.payload.response.status,
      };
    case 'FINISHED_REVOKE_ACCESS':
      return {
        ...state,
        isLoading: false,
        data: [],
        mode: 'sent',
        currentDocumentSource: null,
        errorMessage: action.payload.data.message,
      };
    case 'REMOVE_SENT_RECORD':
      /**
       * Removes the removed sent record from the sent data
       * if the file token and the receiver matches the record is filtered out
       */
      return {
        ...state,
        sent_data: state.sent_data
          .map(record => ((record.file_token === action.payload.recordId && record.send_token === action.payload.sendToken)
            ? { ...record, record_status: 'revoked' }
            : record)),
      };
    case 'REMOVE_RECEIVED_RECORDS':
      return {
        ...state,
        received_data: state.received_data.filter((row) => {
          return !state.archived_records.includes(row.file_token);
        }),
      };
    case 'BEGIN_GET_RECEIVED_RECORDS':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'received',
        errorMessage: null,
        // Clear other page data when switching to received
        uploaded_data: [],
        sent_data: [],
        assigned_data: [],
        referred_data: [],
      };
    case 'FAILED_GET_RECEIVED_RECORDS':
      return {
        ...failedState,
        mode: 'received',
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to get received records.'),
        errorCode: action.payload.response.status,
      };
    case 'FINISHED_GET_RECEIVED_RECORDS':
      // If there are no records, the response is an object with a message key.
      // Here we check if the data is an object (signifying a success/error message)
      // In this case, return an empty array
      if (typeof action.payload.data === 'object' && !Array.isArray(action.payload.data) && action.payload.data !== null) {
        return {
          ...state,
          isLoading: false,
          data: [],
          received_data: [],
          mode: 'received',
          errorMessage: action.payload.data.message,
        };
      }
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        received_data: action.payload.data,
        headers: action.payload.headers,
        mode: 'received',
      };
    case 'BEGIN_GET_REFERRAL_STATUS':
      return {
        ...state,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_GET_REFERRAL_STATUS':
      return {
        ...failedState,
        error: true,
        errorMessage: 'Failed to retrieve referral details.',
      };
    case 'FINISHED_GET_REFERRAL_STATUS':
      return {
        ...state,
        currentReferralStatus: action.payload.data.document_status?.referral_status_id,
        // Full current status row (due_date, slug, linked templates) so the
        // Update Referral modal can preview side effects before a change.
        currentReferredDocumentStatus: action.payload.data.document_status || null,
      };
    case 'BEGIN_GET_REFERRAL_STATUS_COUNTS':
      return {
        ...state,
        referralStatusCountsLoading: true,
        referralStatusCountsError: false,
      };
    case 'FAILED_GET_REFERRAL_STATUS_COUNTS':
      return {
        ...state,
        referralStatusCountsLoading: false,
        referralStatusCountsError: true,
      };
    case 'FINISHED_GET_REFERRAL_STATUS_COUNTS':
      return {
        ...state,
        referralStatusCountsLoading: false,
        referralStatusCountsError: false,
        referralStatusCounts: action.payload.data.status_counts || [],
        referralStatusCountsTotal: action.payload.data.total || 0,
        referralStatusCountsUpdatedAt: action.payload.data.updated_at || null,
      };
    case 'BEGIN_UPDATE_ASSIGNED_RECORD_STATUS':
      return {
        ...state,
        error: false,
      };
    case 'FAILED_UPDATE_ASSIGNED_RECORD_STATUS':
      return {
        ...failedState,
        mode: 'assigned',
        errorMessage: 'There was an issue updating the status. Please try again.',
      };
    case 'FINISHED_UPDATE_ASSIGNED_RECORD_STATUS':
      return {
        ...state,
        isLoading: false,
        updatedStatus: true,
        mode: 'assigned',
      };
    case 'BEGIN_UPDATE_REFERRAL_STATUS':
      return {
        ...state,
        error: false,
        isLoading: true,
      };
    case 'FAILED_UPDATE_REFERRAL_STATUS':
      return {
        ...failedState,
        mode: 'referral',
        isLoading: false,
        errorMessage: 'There was an issue updating the status. Please try again.',
      };
    case 'FINISHED_UPDATE_REFERRAL_STATUS': {
      const { record_id: recordId } = action.payload.data.referred_document_status_id;
      return {
        ...state,
        isLoading: false,
        updatedStatus: true,
        mode: 'referral',
        referredDocumentStatusId: action.payload.data.referred_document_status_id,
        referralStatusCountsRefreshNonce: state.referralStatusCountsRefreshNonce + 1,
        received_data: state.received_data.map((data) => {
          if (data.referral_status?.record_id === recordId) {
            return {
              ...data,
              referral_status: action.payload.data.referred_document_status_id,
            };
          }
          return data;
        }),
      };
    }
    case 'BEGIN_GET_VOICE_DATA':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'voice',
        errorMessage: null,
        // Clear other page data when switching to voice
        uploaded_data: [],
        sent_data: [],
        received_data: [],
        assigned_data: [],
        referred_data: [],
      };
    case 'FAILED_GET_VOICE_DATA':
      return {
        ...failedState,
        mode: 'voice',
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to get voice data.'),
        errorCode: action.payload.response.status,
      };
    case 'FINISHED_GET_VOICE_DATA':
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        voice_data: action.payload.data,
        headers: action.payload.headers,
        mode: 'voice',
      };
    case 'BEGIN_GET_ASSIGNED_RECORDS':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'assigned',
        errorMessage: null,
        // Clear other page data when switching to assigned
        uploaded_data: [],
        sent_data: [],
        received_data: [],
        referred_data: [],
        voice_data: [],
      };
    case 'FAILED_GET_ASSIGNED_RECORDS':
      return {
        ...failedState,
        mode: 'assigned',
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to get assigned records.'),
        errorCode: action.payload.response.status,
      };
    case 'FINISHED_GET_ASSIGNED_RECORDS':
      // If there are no records, the response is an object with a message key.
      // Here we check if the data is an object (signifying a success/error message)
      // In this case, return an empty array
      if (typeof action.payload.data === 'object' && !Array.isArray(action.payload.data.assigned_documents) && action.payload.data !== null) {
        return {
          ...state,
          isLoading: false,
          data: [],
          assigned_data: [],
          department_users: [],
          mode: 'assigned',
          errorMessage: action.payload.data.message,
        };
      }
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        assigned_data: action.payload.data.assigned_documents,
        department_users: action.payload.data.department_users,
        headers: action.payload.headers,
        mode: 'assigned',
      };
    case 'BEGIN_GET_ASSIGNED_RECORD':
      return {
        ...state,
        isLoading: true,
        assignedRecord: {},
        error: false,
        errorMessage: null,
      };
    case 'FAILED_GET_ASSIGNED_RECORD':
      return {
        ...failedState,
        error: true,
        errorMessage: 'There was an issue getting the assigned record. Please try again.',
      };
    case 'FINISHED_GET_ASSIGNED_RECORD':
      return {
        ...state,
        isLoading: false,
        assignedRecord: action.payload.data.assigned_document,
      };
    case 'BEGIN_GET_SINGLE_RECORD_STATUS':
      return {
        ...state,
        isLoading: true,
        currentDocumentSource: null,
        error: false,
        mode: 'sent',
        errorMessage: null,
      };
    case 'FAILED_GET_SINGLE_RECORD_STATUS': {
      const { data: response } = action.payload.response;
      // Checking for status of faxes that don't exist
      if (action.payload.response.status === 400) {
        return {
          ...failedState,
          uploaded_data: state.uploaded_data,
          sent_data: state.sent_data,
          errorMessage: handlingErrors(action.payload.response.error, 'There is no status for this record'),
          errorCode: action.payload.response.status,
        };
      }
      return {
        ...failedState,
        uploaded_data: state.uploaded_data,
        sent_data: state.sent_data,
        errorMessage: response.message || response.error,
      };
    }
    case 'FINISHED_GET_SINGLE_RECORD_STATUS': {
      // If no fax status is available, show as error message
      if (!action.payload.data.receiver) {
        return {
          ...state,
          isLoading: false,
          errorMessage: 'The fax status for this record has not yet been made available',
          currentRecordStatus: null,
        };
      }
      return {
        ...state,
        isLoading: false,
        mode: 'sent',
        currentRecordStatus: action.payload.data,
      };
    }
    case 'FINISHED_PRINT_USER_RECORD': {
      return {
        ...state,
        downloadUrl: action.payload.data.signed_url,
        downloadFilename: action.payload.data.download_filename || 'file',
      };
    }
    case 'BEGIN_PRINT_GUEST_RECORD': {
      return {
        ...initialState,
        isLoading: true,
      };
    }
    case 'FINISHED_PRINT_GUEST_RECORD': {
      return {
        ...state,
        isLoading: false,
        guestAccessUrl: action.payload.data.signed_url,
      };
    }
    case 'FAILED_PRINT_GUEST_RECORD': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to print guest record'),
        errorCode: action.payload.response.status,
      };
    }
    case 'BEGIN_SUBMIT_FORM': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'FINISHED_SUBMIT_FORM': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'FAILED_SUBMIT_FORM': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'BEGIN_CREATE_ASSIGNED_RECORD': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'FINISHED_CREATE_ASSIGNED_RECORD': {
      if (action.payload.data?.is_department_user === false) {
        return {
          ...state,
          isLoading: false,
          isDepartmentUser: false,
        };
      }
      const { assigned_document } = action.payload.data;
      return {
        ...state,
        selectedRecordDetails: {
          ...state.selectedRecordDetails,
          assignee: assigned_document.assignee,
          assignee_email: assigned_document.assignee_email,
          assignee_task: assigned_document.document_status_name,
        },
        received_data: state.received_data.map((data) => {
          const sendCompare = data.send_token && data.send_token === assigned_document.send_token;
          const attachmentCompare = data.attachment_id && data.attachment_id === assigned_document.attachment_id;
          if (sendCompare || attachmentCompare) {
            return {
              ...data,
              assignee: assigned_document.assignee,
              assignee_email: assigned_document.assignee_email,
              assignee_task: assigned_document.document_status_name,
            };
          }
          return data;
        }),
        isLoading: false,
        isDepartmentUser: true,
      };
    }
    case 'FAILED_CREATE_ASSIGNED_RECORD': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'BEGIN_UPDATE_ASSIGNED_RECORD': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'FINISHED_UPDATE_ASSIGNED_RECORD': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'FAILED_UPDATE_ASSIGNED_RECORD': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'BEGIN_UPDATE_RECORD': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FINISHED_UPDATE_RECORD': {
      // A category edit can auto-route the record to a (new) team server-side.
      // When teams is on, the response carries { send_token, team } so we patch
      // the shared list store (received_data — inbox + referrals) and the open
      // viewer, making the team reflect immediately without a page refresh.
      return patchTeamState(
        { ...state, isLoading: false },
        action.payload && action.payload.data,
      );
    }
    case 'FAILED_UPDATE_RECORD': {
      return {
        ...failedState,
        uploaded_data: state.uploaded_data,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.status, 'Unable to update record. Please try again.'),
      };
    }
    case 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT': {
      const { fileToken, requestId } = action.meta;
      const latestRequestId = state.emailAttachmentPatientLatestRequestIds?.[fileToken];
      if (latestRequestId !== undefined && latestRequestId >= requestId) return state;

      return {
        ...state,
        emailAttachmentPatientLatestRequestIds: {
          ...(state.emailAttachmentPatientLatestRequestIds || {}),
          [fileToken]: requestId,
        },
      };
    }
    case 'FINISHED_UPDATE_EMAIL_ATTACHMENT_PATIENT': {
      const { fileToken, requestId } = action.meta;
      if (state.emailAttachmentPatientLatestRequestIds?.[fileToken] !== requestId) return state;

      const canonicalMetadata = patientMetadataFromResponse(action.payload);
      const teamsResponse = teamsResponseFromPatientUpdate(action.payload);
      const rootRecordMatches = state.fileToken === fileToken;
      const nextState = { ...state };

      if (canonicalMetadata) {
        EMAIL_ATTACHMENT_PATIENT_COLLECTION_KEYS.forEach((collectionKey) => {
          if (!Array.isArray(state[collectionKey])) return;
          nextState[collectionKey] = state[collectionKey].map((record) => (
            patchCanonicalPatientMetadata(record, fileToken, canonicalMetadata)
          ));
        });

        if (rootRecordMatches) {
          Object.assign(nextState, canonicalPatientRootFields(canonicalMetadata));
          nextState.selectedRecordDetails = {
            ...state.selectedRecordDetails,
            ...canonicalMetadata,
          };
        }
      }

      return patchTeamState(nextState, teamsResponse);
    }
    case 'BEGIN_UPDATE_RECORD_NOTE': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FINISHED_UPDATE_RECORD_NOTE': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'FAILED_UPDATE_RECORD_NOTE': {
      return {
        ...failedState,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.status, 'Unable to update record. Please try again.'),
      };
    }
    case 'BEGIN_EDIT_NOTIFY': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        updatedTag: false,
        noActiveFax: false,
        noActiveEmail: false,
        departmentName: '',
      };
    }
    case 'FINISHED_EDIT_NOTIFY': {
      const success = action.payload.status !== 204;
      const emailValidMessage = 'We couldn\'t find that user in your organization. To send to that email, try using the send button instead.';

      // Check if department has missing capabilities (can be both)
      const hasNoActiveFax = action.payload.data && action.payload.data.no_active_fax;
      const hasNoActiveEmail = action.payload.data && action.payload.data.no_active_email;

      if (hasNoActiveFax || hasNoActiveEmail) {
        return {
          ...state,
          isLoading: false,
          updatedTag: false,
          noActiveFax: hasNoActiveFax,
          noActiveEmail: hasNoActiveEmail,
          departmentName: action.payload.data.department_name,
          emailValidMessage,
        };
      }

      return {
        ...state,
        isLoading: false,
        updatedTag: success,
        noActiveFax: false,
        noActiveEmail: false,
        departmentName: '',
        emailValidMessage: success ? action.payload.data.message : emailValidMessage,
      };
    }
    case 'FAILED_EDIT_NOTIFY': {
      return {
        ...failedState,
        updatedTag: false,
        noActiveFax: false,
        noActiveEmail: false,
        departmentName: '',
        errorMessage: action.payload.response?.data?.error || action.payload.response,
      };
    }
    case 'BEGIN_EDIT_RECEIVED_RECORD_LABELS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        updatedLabel: false,
      };
    }
    case 'FINISHED_EDIT_RECEIVED_RECORD_LABELS': {
      // A label edit can auto-route the record to a (new) team server-side; the
      // response is the re-serialized record carrying the updated team. Patch the
      // shared list store (received_data — inbox + referrals) and the open viewer
      // so the team reflects immediately without a page refresh.
      const updated = action.payload && action.payload.data;
      const token = updated && updated.send_token;
      if (!token) {
        return { ...state, updatedLabel: true, isLoading: false };
      }
      const patchTeamFields = (record) => (
        record.send_token === token
          ? {
            ...record,
            team: updated.team,
            can_edit_team: updated.can_edit_team,
            team_reassign_visibility_warning: updated.team_reassign_visibility_warning,
          }
          : record
      );
      return {
        ...state,
        updatedLabel: true,
        isLoading: false,
        received_data: (state.received_data || []).map(patchTeamFields),
        selectedRecordDetails: (state.selectedRecordDetails
          && state.selectedRecordDetails.send_token === token)
          ? { ...state.selectedRecordDetails, team: updated.team }
          : state.selectedRecordDetails,
      };
    }
    case 'FAILED_EDIT_RECEIVED_RECORD_LABELS': {
      return {
        ...failedState,
        errorMessage: handlingErrors(action.payload.response.status || '500', 'Unable to update record. Please try again.'),
      };
    }
    case 'CHANGE_SELECTED_LABELS': {
      return {
        ...state,
        selectedLabels: [],
      };
    }
    case 'BEGIN_SET_RECORD_TEAM': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FINISHED_SET_RECORD_TEAM': {
      // Patch the row in the shared list store (received_data backs BOTH the
      // inbox and referral-management tables) plus the open viewer, so the team
      // reflects immediately without depending on a refetch — the slideout has
      // no page-query handle and RecordsHOC may be unmounted behind the viewer.
      const updated = action.payload && action.payload.data;
      const token = updated && updated.send_token;
      if (!token) {
        return { ...state, isLoading: false };
      }
      const patchTeamFields = (record) => (
        record.send_token === token
          ? {
            ...record,
            team: updated.team,
            can_edit_team: updated.can_edit_team,
            team_reassign_visibility_warning: updated.team_reassign_visibility_warning,
          }
          : record
      );
      return {
        ...state,
        isLoading: false,
        received_data: (state.received_data || []).map(patchTeamFields),
        selectedRecordDetails: (state.selectedRecordDetails
          && state.selectedRecordDetails.send_token === token)
          ? { ...state.selectedRecordDetails, team: updated.team }
          : state.selectedRecordDetails,
      };
    }
    case 'FAILED_SET_RECORD_TEAM': {
      return {
        ...failedState,
        errorMessage: handlingErrors(action.payload.response.status || '500', 'Unable to update team. Please try again.'),
      };
    }
    case 'BEGIN_SET_RECORD_TEAM_BULK': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FINISHED_SET_RECORD_TEAM_BULK': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'FAILED_SET_RECORD_TEAM_BULK': {
      return {
        ...failedState,
        errorMessage: handlingErrors(action.payload.response.status || '500', 'Unable to update teams. Please try again.'),
      };
    }
    case 'UPDATE_UPLOADED_RECORDS': {
      // Finds the record  that needs to be updated
      const {
        recordId,
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientPhoneNumber,
        patientDOB,
        mode,
        privacyLevel,
        incorrectPatientInfo,
        documentTitle,
        documentCategory,
        referringProvider,
        referringProviderOffice,
        referralNote,
      } = action.payload;
      const DOB = patientDOB && moment(patientDOB).format('MM/DD/YYYY');
      const dataToUpdate = `${mode}_data`;
      return {
        ...state,
        [dataToUpdate]: state[dataToUpdate].map((data) => {
          if (data.file_token === recordId) {
            return {
              ...data,
              patient_name: patientName,
              patient_first_name: patientFirstName,
              patient_middle_name: patientMiddleName,
              patient_last_name: patientLastName,
              ...(patientPhoneNumber !== undefined ? { patient_phone_number: patientPhoneNumber } : {}),
              patient_dob: DOB,
              privacy_level: privacyLevel,
              has_wrong_extraction_been_reported: incorrectPatientInfo,
              patient_info_edited: true,
              document_title: documentTitle,
              document_category: documentCategory,
              referring_provider: referringProvider,
              provider_office: referringProviderOffice,
              referral_note: referralNote,
            };
          }
          return data;
        }),
        patientName,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        ...(patientPhoneNumber !== undefined ? { patientPhoneNumber } : {}),
        privacyLevel,
        patientDOB: DOB,
        documentTitle,
        documentCategory,
        referringProvider,
        referringProviderOffice,
        hasWrongExtractionBeenReported: state.hasWrongExtractionBeenReported || incorrectPatientInfo,
      };
    }
    case 'UPDATE_UPLOADED_RECORD_PRIVACY': {
      const {
        recordId,
        mode,
        privacyLevel,
      } = action.payload;
      const updateRecordPrivacy = (data) => (
        data.file_token === recordId ? {
          ...data,
          privacy_level: privacyLevel,
        } : data
      );
      const nextState = {
        ...state,
        privacyLevel,
      };

      recordPrivacyCollectionKeysForMode(mode).forEach((collectionKey) => {
        if (!Array.isArray(state[collectionKey])) return;
        nextState[collectionKey] = state[collectionKey].map(updateRecordPrivacy);
      });

      return nextState;
    }
    case 'UPDATE_RECEIVED_RECORDS': {
      const {
        sendToken, tag, labels, isLabelIncorrect,
      } = action.payload;
      const infoToUpdate = tag || labels;
      const stateToUpdate = tag ? 'tag' : 'labels';
      const objectType = state.data.assigned_documents ? 'assigned_data' : 'received_data';
      const objects = state.data.assigned_documents ? state.assigned_data : state.received_data;
      return {
        ...state,
        [objectType]: objects.map((data) => {
          if (data.send_token === sendToken || sendToken.includes(data.send_token)) {
            return {
              ...data,
              [stateToUpdate]: infoToUpdate,
              has_wrong_labeling_been_reported: isLabelIncorrect,
              is_label_edited: stateToUpdate === 'labels',
            };
          }
          return data;
        }),
        tag,
        hasWrongLabelingBeenReported: isLabelIncorrect,
        isLabelEdited: stateToUpdate === 'labels',
        selectedRecordDetails: {
          ...state.selectedRecordDetails,
          labels,
        },
      };
    }
    case 'MARK_RECORD_AS_VIEWED': {
      const { sendToken } = action.payload;
      return {
        ...state,
        received_data: state.received_data.map((data) => {
          if (data.send_token === sendToken || sendToken.includes(data.send_token)) {
            return {
              ...data,
              viewed: true,
            };
          }
          return data;
        }),
        assigned_data: state.assigned_data.map((data) => {
          if (data.send_token === sendToken || sendToken.includes(data.send_token)) {
            return {
              ...data,
              viewed: true,
            };
          }
          return data;
        }),
      };
    }
    case 'BEGIN_GET_RECORD_LINK': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
        secure_link: '',
      };
    }
    case 'FINISHED_GET_RECORD_LINK': {
      return {
        ...state,
        isLoading: false,
        secure_link: action.payload.data.secure_link,
      };
    }
    case 'FAILED_GET_RECORD_LINK': {
      return {
        ...failedState,
        isLoading: false,
        secure_link: '',
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to get record link'),
      };
    }
    case 'BEGIN_GET_SECURE_URL': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: '',
        secure_signed_url: '',
        hasResponse: false,
        // Reset per fetch so the flag is payload-authoritative on the link
        // surface: a prior flagged link's `true` must never carry into a later
        // link whose branch omits the field (e.g. api-source / record-owner
        // links), which would route an unscoped link to the Nutrient viewer.
        nutrientViewer: false,
      };
    }
    case 'FINISHED_GET_SECURE_URL': {
      return {
        ...state,
        isLoading: false,
        canEditRecord: action.payload.data.can_edit,
        secure_signed_url: action.payload.data.signed_url,
        documentType: action.payload.data.document_type || state.documentType,
        subject: action.payload.data.subject,
        recipient_email: action.payload.data.recipient_email,
        recipient_name: action.payload.data.recipient_name,
        send_token: action.payload.data.send_token,
        hasResponse: action.payload.data.has_reply,
        signatureRequired: action.payload.data.signature_required,
        shouldFlatten: action.payload.data.should_flatten,
        // Payload-authoritative: BEGIN_GET_SECURE_URL reset this to false, so a
        // branch that omits the field stays false (old-viewer-safe) rather than
        // inheriting a prior link's value. Only an explicit boolean from the
        // sending org's scoped payload routes to the Nutrient viewer.
        nutrientViewer: typeof action.payload.data.nutrient_viewer === 'boolean'
          ? action.payload.data.nutrient_viewer
          : state.nutrientViewer,
        nameToSignatureRequired: action.payload.data.name_to_signature_required,
        ownerType: action.payload.data.owner_type,
        ownerId: action.payload.data.owner_id,
      };
    }
    case 'FAILED_GET_SECURE_URL': {
      return {
        ...failedState,
        isLoading: false,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: action.payload.response.data.error,
      };
    }
    case 'BEGIN_PRINT_SECURE_URL': {
      return {
        ...state,
      };
    }
    case 'FINISHED_PRINT_SECURE_URL': {
      return {
        ...state,
        isLoading: false,
        printUrl: action.payload.data.signed_url,
      };
    }
    case 'FAILED_PRINT_SECURE_URL': {
      return {
        ...failedState,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error),
      };
    }
    case 'BEGIN_MERGE_RECORDS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_MERGE_RECORDS': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to merge records. Please try again'),
      };
    }
    case 'FINISHED_MERGE_RECORDS': {
      return {
        ...state,
        isLoading: false,
        uploaded_data: [action.payload.data, ...state.uploaded_data],
      };
    }
    case 'BEGIN_MERGE_REFERRALS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_MERGE_REFERRALS': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to merge referrals. Please try again'),
      };
    }
    case 'FINISHED_MERGE_REFERRALS': {
      return {
        ...state,
        isLoading: false,
        received_data: [action.payload.data, ...state.received_data],
      };
    }
    case 'BEGIN_RESEND_RECORD': {
      return {
        ...state,
        isLoading: true,
        isResendingUnviewed: true,
      };
    }
    case 'FINISHED_RESEND_ONE_RECORD': {
      // The record that was resent is the most recent, which is why
      // it is the 0th index in the data returned by Rails
      const { file_token: resentFileToken, send_token } = action.payload.data.sent_data[0];
      const { sent_data: dataCurrentlyOnPage } = state;
      return {
        ...state,
        isLoading: false,
        isResendingUnviewed: false,
        // After a record is resent, the send token, which allows users to view the status of a
        // record, changes. In order for users to view the status of the latest resend, we need to
        // update the frontend values with the new send token sent by Rails, that way a refresh is
        // not required
        //
        // Loop through all data on page and update record that was resent
        sent_data: dataCurrentlyOnPage.map((row) => {
          if (row.file_token === resentFileToken) {
            return {
              ...row,
              send_token, // Replace with new send token
            };
          }
          return row;
        }),
      };
    }
    case 'FINISHED_RESEND_RECORD': {
      return {
        ...state,
        isLoading: false,
        isResendingUnviewed: false,
        totalRecordsResent: action.payload.data.number_resent,
      };
    }
    case 'FAILED_RESEND_RECORD': {
      return {
        ...failedState,
        isResendingUnviewed: true,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to resend the record(s)'),
      };
    }
    case 'BEGIN_RESEND_ALL_FAILED_FAXES':
      return {
        ...state,
        isLoading: true,
        isResendingFailed: true,
      };
    case 'FAILED_RESEND_ALL_FAILED_FAXES':
      return {
        ...state,
        isLoading: false,
        isResendingFailed: false,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to resend the records'),
      };
    case 'FINISHED_RESEND_ALL_FAILED_FAXES':
      return {
        ...state,
        // Modify failed faxes to have status 'inprogress'.
        sent_data: state.sent_data.map(x => ({
          ...x,
          fax_status: ['failure', 'partialsuccess'].includes(x.fax_status) && x.fax_error_message !== 'No fax tone detected' ? 'inprogress' : x.fax_status,
        })),
        isLoading: false,
        isResendingFailed: false,
        totalRecordsResent: action.payload.data.number_resent,
      };
    case '@@router/LOCATION_CHANGE': {
      const { pathname } = action.payload;
      if (!pathname.includes('app/view/')) {
        return {
          ...state,
          patientName: '',
          patientDOB: '',
          patientMrn: '',
          secure_link: '',
          secure_signed_url: '',
          retrying: false,
          currentDocumentSource: null,
          currentRecordId: null,
          currentRecordRequest: initialCurrentRecordRequest,
          canEditRecord: false,
        };
      }
      return state;
    }
    case 'PROGRESS_DOWNLOAD_RECORDS_ZIP':
      return {
        ...state,
        total: action.payload.total,
        progress: action.payload.loaded,
      };
    case 'FAILED_DOWNLOAD_RECORDS_ZIP':
      return {
        ...state,
        total: 0,
        progress: 0,
        downloadError: action.payload.error,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.error, 'Failed to download records.'),
      };
    // Equivalent to FINISHED_RECORDS_DOWNLOAD_ZIP
    // This is used because the values should be reset after the download dialog is closed.
    // Resetting before the values close would show wrong values in the progress indicator.
    case 'HIDE_MODAL':
      return {
        ...state,
        total: 0,
        progress: 0,
        downloadError: undefined,
      };
    case 'SELECTED_ROWS':
      return {
        ...state,
        total: 0,
        progress: 0,
        downloadError: undefined,
      };
    case 'UPDATE_SELECTED_RECORD': {
      return {
        ...state,
        patientName: action.payload.patient_name,
        patientDOB: action.payload.patient_dob,
        privacyLevel: action.payload.privacy_level,
      };
    }
    case 'BEGIN_SAVE_ANNOTATIONS_ON_RECORD': {
      if (action.meta?.requestOwnership) {
        const currentRecordRequest = currentRecordRequestForSelectionSaveBegin(
          state.currentRecordRequest,
          action,
        );
        if (currentRecordRequest === state.currentRecordRequest) return state;

        return {
          ...state,
          currentRecordRequest,
        };
      }
      if (state.currentRecordRequest?.mode === 'tokenized') return state;

      return {
        ...state,
        isLoading: true,
      };
    }
    case 'FAILED_SAVE_ANNOTATIONS_ON_RECORD': {
      if (!selectionSaveMatchesCurrentRecordRequest(state.currentRecordRequest, action)) return state;
      if (action.meta?.requestOwnership) {
        return {
          ...state,
          currentRecordRequest: settleCurrentRecordSelectionSave(state.currentRecordRequest),
        };
      }

      return {
        ...failedState,
        error: true,
        errorCode: action.payload?.response?.status,
        errorMessage: action.payload?.response?.error,
        recordHasUnsavedEdits: true,
      };
    }
    case 'FINISHED_SAVE_ANNOTATIONS_ON_RECORD': {
      if (!selectionSaveMatchesCurrentRecordRequest(state.currentRecordRequest, action)) return state;

      return {
        ...state,
        isLoading: false,
        ...(action.meta?.requestOwnership && {
          currentRecordRequest: {
            ...state.currentRecordRequest,
            selectionSaveSettled: true,
          },
        }),
        fileToken: action.payload.data.file_token,
        currentDocumentSource: action.payload.data.signed_url,
        recordHasUnsavedEdits: false,
        numPages: action.payload.data.num_pages,
      };
    }
    case 'BEGIN_GET_NEXT_RECORD':
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FINISHED_GET_NEXT_RECORD':
      return {
        ...state,
        nextSendToken: action.payload.data.send_token,
        nextFileToken: action.payload.data.file_token,
        isLoading: false,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_GET_NEXT_RECORD':
      return {
        ...failedState,
        errorMessage: action.payload.response.data.error,
        error: true,
        errorCode: action.payload.response.status,
      };
    case 'SET_UNSAVED_EDITS_ON_RECORD': {
      return {
        ...state,
        recordHasUnsavedEdits: true,
      };
    }
    case 'BEGIN_SAVE_SIGNATURE': {
      return {
        ...state,
      };
    }
    case 'FAILED_SAVE_SIGNATURE': {
      return {
        ...failedState,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: action.payload.response.error,
      };
    }
    case 'FINISHED_SAVE_SIGNATURE': {
      return {
        ...state,
      };
    }
    case 'BEGIN_SPLIT_RECORD':
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_SPLIT_RECORD':
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response.data.error,
      };
    case 'FINISH_SPLIT_RECORD':
      return {
        ...state,
        isLoading: false,
        fileToken: action.payload.data.file_token,
      };
    case 'BEGIN_GET_SIGNATURE': {
      return {
        ...state,
      };
    }
    case 'FAILED_GET_SIGNATURE': {
      return {
        ...failedState,
        error: true,
        errorCode: action.payload.response?.status,
        errorMessage: action.payload.response?.error,
        signatureData: state.signatureData,
      };
    }
    case 'FINISHED_GET_SIGNATURE': {
      return {
        ...state,
        signatureData: normalizeSignatureData(action.payload.data),
      };
    }
    case 'BEGIN_GET_ATTACHMENTS': {
      return {
        ...state,
        error: false,
      };
    }
    case 'FAILED_GET_ATTACHMENTS': {
      return {
        ...failedState,
        error: true,
        errorCode: action.payload.response.status,
        errorMessage: action.payload.response.error,
      };
    }
    case 'FINISHED_GET_ATTACHMENTS': {
      return {
        ...state,
        attachmentsData: action.payload.data,
      };
    }
    case 'RETRYING_REQUEST': {
      return {
        ...state,
        retrying: true,
        retryAttemptNumber: action.payload.retries,
      };
    }
    case 'RETRYING_FAILED': {
      return {
        ...state,
        retrying: false,
        retryAttemptNumber: 0,
      };
    }
    case 'BEGIN_UPLOAD_TO_SFTP_FOLDER': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_UPLOAD_TO_SFTP_FOLDER': {
      return {
        ...state,
        error: true,
        isLoading: false,
        errorCode: action.payload.response.status,
        errorMessage: action.payload.response.data.error,
      };
    }
    case 'FINISH_UPLOAD_TO_SFTP_FOLDER': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'BEGIN_AUTOUPLOAD': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_AUTOUPLOAD': {
      let errorMessage = action.payload.response.data.error;
      // ENG-3012: the reply chart endpoints return explicit, deterministic 422
      // messages. Rewriting them into the missing-credential instructions
      // below would misreport a document problem as an account-setup problem.
      const explicitReplyChartErrors = [
        'TIFF reply attachments are not currently supported for chart upload.',
        'Reply has no uploadable document',
        'A document could not be prepared for chart upload.',
      ];
      if (action.payload.response.status === 422 && !explicitReplyChartErrors.includes(errorMessage)) {
        // Assume EMR credential does not exist.

        // Get org name
        let orgName = localStorage.getItem('m8_org');
        if (!orgName || orgName === 'undefined') {
          orgName = Date.now().toString(36).slice(0, 6);
        } else {
          orgName = orgName.replace('^A-Za-z0-9', ' ').toLowerCase().replace(/\s+/g, '');
        }
        errorMessage = `In order to enable this feature, please create an account with the email address emr+${orgName}@medsender.com in your EMR and email them to support@medsender.com.`;
      }

      return {
        ...state,
        error: true,
        isLoading: false,
        errorCode: action.payload.response.status,
        errorMessage,
      };
    }
    case 'FINISH_AUTOUPLOAD': {
      const newState = {
        ...state,
        isLoading: false,
        error: false,
        errorMessage: null,
      };

      // Handle both old format (emr_upload wrapper) and new format (direct response)
      // Old format: action.payload.data.emr_upload
      // New format: action.payload.data (from chart_uploads endpoint)
      const uploadData = action.payload.data.emr_upload || action.payload.data;

      const recordPayload = uploadData.record;

      const targetSendToken = uploadData.send_token;
      const hasRecordPayload = recordPayload && typeof recordPayload === 'object';
      // For modern EMRs (chart_uploads): response has nested 'record' object with user's form input
      //   -> Trust recordPayload values, even if empty (user intentionally cleared)
      // For legacy EMRs (emr_uploads/CareLogic): no 'record' object in response
      //   -> Use uploadData which has preserved values from EmrUpload
      const updates = {
        patient_first_name: hasRecordPayload ? recordPayload.patient_first_name : uploadData.patient_first_name,
        patient_middle_name: hasRecordPayload ? recordPayload.patient_middle_name : uploadData.patient_middle_name,
        patient_last_name: hasRecordPayload ? recordPayload.patient_last_name : uploadData.patient_last_name,
        patient_name: hasRecordPayload ? recordPayload.patient_name : uploadData.patient_name,
        patient_dob: hasRecordPayload ? recordPayload.patient_dob : uploadData.patient_dob,
        mrn: hasRecordPayload ? recordPayload.mrn : uploadData.mrn,
        document_title: hasRecordPayload ? (recordPayload.document_title || uploadData.file_name) : (uploadData.file_name || uploadData.document_title),
      };

      const sanitizedUpdates = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined && value !== null),
      );

      if (hasRecordPayload && targetSendToken) {
        const updateEntryBySendToken = (entry) => {
          if (!entry) return entry;

          if (entry.send_token === targetSendToken) {
            return { ...entry, ...sanitizedUpdates };
          }

          return entry;
        };

        const collections = ['received_data', 'assigned_data', 'referred_data', 'uploaded_data'];

        collections.forEach((collectionKey) => {
          if (!Array.isArray(newState[collectionKey])) return;
          const updatedCollection = newState[collectionKey].map(updateEntryBySendToken);
          newState[collectionKey] = updatedCollection;
        });

        if (Array.isArray(newState.data)) {
          newState.data = newState.data.map(updateEntryBySendToken);
        } else if (newState.data?.assigned_documents) {
          newState.data = {
            ...newState.data,
            assigned_documents: newState.data.assigned_documents.map(updateEntryBySendToken),
          };
        }
      } else if (targetSendToken) {
        const index = state.received_data.findIndex(data => data.send_token === targetSendToken);

        if (index !== -1) {
          newState.received_data = [
            ...newState.received_data.slice(0, index),
            {
              ...newState.received_data[index],
              ...sanitizedUpdates,
            },
            ...newState.received_data.slice(index + 1)
          ];
        }
      }

      const responseUpload = action.payload.data.emr_upload || action.payload.data || {};
      const responseRecord = responseUpload.record || {};

      if (state.sendToken && responseUpload.send_token && state.sendToken === responseUpload.send_token) {
        newState.patientFirstName = responseRecord.patient_first_name || responseUpload.patient_first_name;
        newState.patientMiddleName = responseRecord.patient_middle_name || responseUpload.patient_middle_name;
        newState.patientLastName = responseRecord.patient_last_name || responseUpload.patient_last_name;
        newState.patientName = responseRecord.patient_name || responseUpload.patient_name;
        newState.patientDOB = responseRecord.patient_dob || responseUpload.patient_dob;
        newState.patientMrn = responseRecord.mrn || responseUpload.mrn;
      }

      return newState;
    }
    case 'BEGIN_GET_REFERRAL_REPLY_FAX_URL':
      return {
        ...state,
        isLoading: true,
      };
    case 'FAILED_GET_REFERRAL_REPLY_FAX_URL':
      return {
        ...state,
        isLoading: false,
        error: true,
      };
    case 'FINISHED_GET_REFERRAL_REPLY_FAX_URL':
      return {
        ...state,
        isLoading: false,
        error: false,
        attachmentUrl: action.payload.data.signed_url,
      };
    case 'BEGIN_GET_REFERRAL_REPLY_PREVIEW_URL':
      return {
        ...state,
        isLoading: true,
      };
    case 'FAILED_GET_REFERRAL_REPLY_PREVIEW_URL':
      return {
        ...state,
        isLoading: false,
        error: true,
      };
    case 'FINISHED_GET_REFERRAL_REPLY_PREVIEW_URL':
      return {
        ...state,
        isLoading: false,
        error: false,
        previewUrl: action.payload.data,
      };
    case 'BEGIN_GET_REFERRAL_REPLIES':
      return {
        ...state,
        isLoading: true,
      };
    case 'FAILED_GET_REFERRAL_REPLIES':
      return {
        ...state,
        isLoading: false,
        error: true,
      };
    case 'FINISHED_GET_REFERRAL_REPLIES':
      return {
        ...state,
        isLoading: false,
        error: false,
        referralReplies: action.payload.data,
      };
    case 'BEGIN_SEND_REFERRAL_REPLY': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_SEND_REFERRAL_REPLY': {
      return {
        ...failedState,
        error: true,
        isLoading: false,
        errorMessage: action.payload.response.data.error,
        errorCode: action.payload.response.status,
      };
    }
    case 'FINISH_SEND_REFERRAL_REPLY': {
      return {
        ...state,
        isLoading: false,
      };
    }
    case 'BEGIN_SMSMESSAGE': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_SMSMESSAGE': {
      return {
        ...state,
        error: true,
        isLoading: false,
        errorCode: action.payload.response.status,
        errorMessage: action.payload.response.data.error,
      };
    }
    case 'FINISH_SMSMESSAGE': {
      return {
        ...state,
        optedOut: action.payload.data.opted_out,
        isLoading: false,
      };
    }
    case 'BEGIN_TASK_CREATION': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: null,
      };
    }
    case 'FAILED_TASK_CREATION': {
      let errorMessage = action.payload.response.data.error;
      if (action.payload.response.status === 422) {
        // Assume EMR credential does not exist.

        // Get org name
        let orgName = localStorage.getItem('m8_org');
        if (!orgName || orgName === 'undefined') {
          orgName = Date.now().toString(36).slice(0, 6);
        } else {
          orgName = orgName.replace('^A-Za-z0-9', ' ').toLowerCase().replace(/\s+/g, '');
        }
        errorMessage = `In order to enable task creation, please create an account with the email address emr+${orgName}@medsender.com in your EMR and email them to support@medsender.com.`;
      }

      return {
        ...state,
        error: true,
        isLoading: false,
        errorCode: action.payload.response.status,
        errorMessage,
      };
    }
    case 'FINISH_TASK_CREATION': {
      const newState = {
        ...state,
        isLoading: false,
      };

      const taskUpdates = {
        patient_first_name: action.payload.data.emr_upload.patient_first_name,
        patient_middle_name: action.payload.data.emr_upload.patient_middle_name,
        patient_last_name: action.payload.data.emr_upload.patient_last_name,
        patient_name: action.payload.data.emr_upload.patient_name,
        patient_dob: action.payload.data.emr_upload.patient_dob,
        document_title: action.payload.data.emr_upload.file_name,
      };

      ['received_data', 'uploaded_data'].forEach((collectionKey) => {
        if (!Array.isArray(newState[collectionKey])) return;
        const idx = newState[collectionKey].findIndex((data) => data.send_token === action.payload.data.emr_upload.send_token);
        if (idx !== -1) {
          newState[collectionKey] = [
            ...newState[collectionKey].slice(0, idx),
            { ...newState[collectionKey][idx], ...taskUpdates },
            ...newState[collectionKey].slice(idx + 1),
          ];
        }
      });

      // Also update the individual Redux fields used by RecordViewer
      // Check if this upload is for the currently viewed record
      if (state.sendToken === action.payload.data.emr_upload.send_token) {
        newState.patientFirstName = action.payload.data.emr_upload.patient_first_name;
        newState.patientMiddleName = action.payload.data.emr_upload.patient_middle_name;
        newState.patientLastName = action.payload.data.emr_upload.patient_last_name;
        newState.patientName = action.payload.data.emr_upload.patient_name;
        newState.patientDOB = action.payload.data.emr_upload.patient_dob;
        newState.patientMrn = action.payload.data.emr_upload.mrn;
      }

      return newState;
    }
    case 'SET_SELECTED_RECORD_DETAILS': {
      return {
        ...state,
        selectedRecordDetails: action.payload.selectedRecordDetails,
        currentRecordId: action.payload.currentRecordId,
        currentDocumentSource: null, // Clear old PDF to prevent showing stale document during loading
        currentRecordRequest: initialCurrentRecordRequest,
        canEditRecord: false,
        fileToken: '',
        // The detail rows are PHI too, and this dispatch is where the record identity
        // actually changes — one commit before the fetch's BEGIN gets to clear them.
        ...CLEARED_RECORD_DETAIL_FIELDS,
      };
    }
    case 'SET_CURRENT_RECORD_ID': {
      return {
        ...state,
        currentRecordId: action.payload.recordId,
      };
    }
    case 'CLEAR_CURRENT_RECORD_ID': {
      return {
        ...state,
        currentRecordId: null,
        currentDocumentSource: null,
        currentRecordRequest: initialCurrentRecordRequest,
        canEditRecord: false,
        fileToken: '',
        sendToken: null,
      };
    }
    case 'CLEAR_SELECTED_RECORD_DETAILS': {
      return {
        ...state,
        selectedRecordDetails: {},
        currentDocumentSource: null,
        currentRecordId: null,
        currentRecordRequest: initialCurrentRecordRequest,
        canEditRecord: false,
        fileToken: '',
        documentTitle: '',
        patientName: '',
        patientDOB: '',
        patientMrn: '',
        documentCategory: '',
        // Reset error/loading state left by single-record fetches so the
        // main page isn't polluted after the viewer closes.
        error: false,
        errorMessage: null,
        errorCode: null,
        isLoading: false,
      };
    }
    case 'CLEAR_DOWNLOAD_URL': {
      return {
        ...state,
        downloadUrl: null,
        downloadFilename: null,
      };
    }
    case 'UPDATE_RECORD_INFO': {
      return {
        ...state,
        ...action.payload,
      };
    }
    case 'BEGIN_UPDATE_REFERRAL_DUE_DATE': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'FAILED_UPDATE_REFERRAL_DUE_DATE': {
      return {
        ...state,
        isLoading: false,
        error: true,
      };
    }
    case 'FINISHED_UPDATE_REFERRAL_DUE_DATE': {
      const recordId = action.payload.data.referred_document_status.record_id;
      
      return {
        ...state,
        isLoading: false,
        updatedStatus: true,
        mode: 'referral',
        referredDocumentStatusId: action.payload.data.referred_document_status.due_date,
        received_data: state.received_data.map((data) => {
          if (data.referral_status?.record_id === recordId) {
            return {
              ...data,
              referral_status: {
                ...data.referral_status,
                due_date: action.payload.data.referred_document_status.due_date
              }
            };
          }
          return data;
        }),
      };
    }
    case 'BEGIN_FETCH_DOCUMENT_COMMENTS':
      return {
        ...state,
        documentCommentsLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_FETCH_DOCUMENT_COMMENTS':
      if (!documentCommentsResponseIsCurrent(state, action)) return state;
      return {
        ...state,
        documentCommentsLoading: false,
        error: true,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to fetch document comments'),
      };
    case 'FINISHED_FETCH_DOCUMENT_COMMENTS':
      if (!documentCommentsResponseIsCurrent(state, action)) return state;
      return {
        ...state,
        documentCommentsLoading: false,
        documentComments: action.payload.data.document_comments,
      };
    case 'BEGIN_CREATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_CREATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        error: true,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to create document comment'),
      };
    case 'FINISHED_CREATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        documentComments: action.payload.data.document_comments,
      };
    case 'BEGIN_UPDATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_UPDATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        error: true,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to update document comment'),
      };
    case 'FINISHED_UPDATE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        documentComments: action.payload.data.document_comments
      };
    case 'BEGIN_DELETE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: true,
        error: false,
        errorMessage: null,
      };
    case 'FAILED_DELETE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        error: true,
        errorMessage: handlingErrors(action.payload.response.status, 'Failed to delete document comment'),
      };
    case 'FINISHED_DELETE_DOCUMENT_COMMENT':
      return {
        ...state,
        documentCommentsLoading: false,
        documentComments: state.documentComments.filter(comment => 
          comment.id !== action.payload.data.id
        ),
      };
    case 'CLEAR_DOCUMENT_COMMENTS':
      return {
        ...state,
        documentComments: [],
        documentCommentsLoading: false,
      };
    case 'REQUEST_INBOX_TABLE_REFRESH':
      // Watched by RecordsHOC: each bump triggers a silent background refetch of
      // the current inbox query (see requestInboxTableRefresh in actions/records).
      // The action's `reason` is deliberately not stored: it is diagnostic only
      // (Redux devtools / action logs). Every refresh refetches the current query
      // identically regardless of what triggered it — don't branch on reason.
      return {
        ...state,
        inboxRefreshNonce: state.inboxRefreshNonce + 1,
      };
    case 'REFERRAL_AUTO_ARCHIVED': {
      const { recordId } = action.payload;
      
      return {
        ...state,
        referralStatusCountsRefreshNonce: state.referralStatusCountsRefreshNonce + 1,
        received_data: state.received_data.filter((data) => {
          return data.send_token !== recordId && data.attachment_id !== recordId;
        }),
      };
    }
    default:
      return state;
  }
}

export default (state, action) => {
  const currentState = state || initialState;
  const nextState = reduceRecords(currentState, action);
  if (action.type === 'USER_LOGGED_OUT'
    || action.type === 'BEGIN_UPDATE_EMAIL_ATTACHMENT_PATIENT'
    || nextState.emailAttachmentPatientLatestRequestIds
      === currentState.emailAttachmentPatientLatestRequestIds) return nextState;

  return {
    ...nextState,
    emailAttachmentPatientLatestRequestIds:
      currentState.emailAttachmentPatientLatestRequestIds || {},
  };
};
